import { apiFetch } from '@/shared/api/client';

/**
 * Stock por unidades con origen: cada unidad (con serie) o ingreso (sin serie)
 * sabe de qué compra, importación y remito vino. Corre en paralelo a los
 * contadores de Inventario (ver server/src/lib/stockUnidades.ts).
 * `habilitado: false` = la migración no está aplicada: las pantallas lo ocultan.
 */

export type OrigenUnidad = 'compra' | 'stock_inicial' | 'ajuste';

export type UnidadStock = {
  id: number;
  articleId: number;
  origen: OrigenUnidad;
  fechaIngreso: string;
  nroSerie: string | null;
  nroDespacho: string | null;
  cantidadIngresada: number;
  cantidadDisponible: number;
  cantidadPrestada: number;
  estado: 'en_stock' | 'en_prestamo' | 'agotada';
  observaciones: string | null;
  compra: { id: string; proveedor: string; comprobante: string } | null;
  importacion: { id: number; numero: number; descripcion: string } | null;
  recepcionId: number | null;
  nroRemito: string | null;
  /** Sólo admin / administración. */
  costoUnitario?: number | null;
  moneda?: string | null;
};

export type TotalPorOrigen = {
  importacionId: number | null;
  numero: number | null;
  descripcion: string | null;
  etiqueta: string;
  ingresada: number;
  disponible: number;
  prestada: number;
};

export type UnidadesArticuloResponse = {
  habilitado: boolean;
  articulo: { id: string; nombre: string; serializado: boolean; impactaStock: boolean } | null;
  unidades: UnidadStock[];
  /** Despachos (y anulaciones) de esas unidades: a qué venta fue cada una. */
  salidas: SalidaUnidad[];
  porImportacion: TotalPorOrigen[];
  totales: { ingresada: number; disponible: number; prestada: number };
};

export type SalidaUnidad = {
  movimientoId: number;
  unidadId: number;
  tipo: 'despacho' | 'anulacion_despacho' | 'prestamo' | 'devolucion_prestamo' | 'ajuste_salida';
  fecha: string;
  cantidad: number;
  ventaId: string | null;
  cliente: string | null;
  nroSerie: string | null;
  automatico: boolean;
};

export type UnidadesOrigenResponse = {
  habilitado: boolean;
  unidades: Array<UnidadStock & { articulo: string }>;
  salidas: SalidaUnidad[];
  resumen: { ingresada: number; disponible: number; prestada: number; despachada: number };
};

export type FilaConciliacionUnidades = {
  articleId: number;
  articuloId: string | null;
  articulo: string;
  tipo: string;
  serializado: boolean;
  deposito: { contadores: number; unidades: number; diferencia: number };
  prestado: { contadores: number; unidades: number; diferencia: number };
  porIngresar: number;
  unidadesVivas: number;
  nota: string | null;
};

export type ConciliacionUnidadesResponse = {
  habilitado: boolean;
  resumen: { articulos: number; conDiferencia: number; unidadesAuditadas: number; unidadesDescuadradas: number };
  diferencias: FilaConciliacionUnidades[];
  unidadesDescuadradas: Array<{ unidadId: number; articleId: number; diferencia: number }>;
};

export const getUnidadesArticulo = (inventarioId: string) =>
  apiFetch<UnidadesArticuloResponse>(`/api/inventario/${encodeURIComponent(inventarioId)}/unidades`);

export const getUnidadesCompra = (compraId: string) =>
  apiFetch<UnidadesOrigenResponse>(`/api/compras/${encodeURIComponent(compraId)}/unidades`);

export const getUnidadesImportacion = (importacionId: number | string) =>
  apiFetch<UnidadesOrigenResponse>(`/api/importaciones/${encodeURIComponent(String(importacionId))}/unidades`);

export const getConciliacionUnidades = () =>
  apiFetch<ConciliacionUnidadesResponse>('/api/inventario/unidades-conciliacion');

/** yyyy-mm-dd → dd/mm/yyyy. */
export const fechaUnidad = (iso: string | null | undefined) =>
  iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—';

/** Origen en una línea: "Importación #3 · Compra 123", "Compra 123", "Stock inicial (sin origen)". */
export function etiquetaOrigen(u: Pick<UnidadStock, 'origen' | 'compra' | 'importacion'>): string {
  if (u.origen === 'stock_inicial') return 'Stock inicial (sin origen)';
  if (u.origen === 'ajuste') return 'Ajuste manual';
  const partes: string[] = [];
  if (u.importacion) partes.push(`Importación #${u.importacion.numero}`);
  if (u.compra) partes.push(`Compra ${u.compra.id}`);
  return partes.join(' · ') || 'Compra';
}

export const ESTADO_UNIDAD: Record<UnidadStock['estado'], string> = {
  en_stock: 'En stock',
  en_prestamo: 'En préstamo',
  agotada: 'Agotada',
};

const TIPO_SALIDA: Record<SalidaUnidad['tipo'], string> = {
  despacho: 'Despacho',
  anulacion_despacho: 'Despacho anulado',
  prestamo: 'Préstamo / showroom',
  devolucion_prestamo: 'Devolución',
  ajuste_salida: 'Ajuste',
};
export const etiquetaSalida = (t: SalidaUnidad['tipo']) => TIPO_SALIDA[t];
